import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import {
  Container,
  Divider,
  Header,
  Loader,
  Pagination,
  Table
} from "semantic-ui-react";

import {
  getURLFilterParams,
  updateFilterSearchParams,
  updatePageSearchParams
} from "../../utils/utils.js";

import UnauthorisedFeature from "../../components/auth/UnauthorisedFeature.jsx";
import RedirectWithReset from "../../components/utils/RedirectWithReset.jsx";
import ImageFilters from "../../components/images/ImageFilters.jsx";
import ImageRow from "../../components/images/ImageRow.jsx";
import { MediaAs } from "../../styles/Media.js";

import withConfirmationModal from "../../components/modals/withConfirmationModal.js";

import { ENTITIES } from "../../state/actions/actionConstants.js";
import { OPERATIONS } from "../../constants/operations.js";
import { AT_TYPES } from "../../constants/entity.js";

import { getImages } from "../../state/reducers/images/imageActions.js";
import { operationAction } from "../../state/reducers/operations/operationActions.js";

import imageSelector from "../../state/reducers/images/imageSelector.js";
import { selectOperationAvailability } from "../../state/reducers/endpoints/endpointSelector.js";
import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class Images extends Component {
  static propTypes = {
    copyKey: PropTypes.string.isRequired,

    data: PropTypes.array,
    page: PropTypes.number,
    totalPages: PropTypes.number,
    isFetching: PropTypes.bool.isRequired,
    isErrored: PropTypes.bool,
    listImagesAvailability: PropTypes.object.isRequired,
    copy: PropTypes.func.isRequired,

    openModal: PropTypes.func.isRequired,
    closeModal: PropTypes.func.isRequired,

    getImages: PropTypes.func.isRequired,
    operationAction: PropTypes.func.isRequired
  };

  static defaultProps = {
    copyKey: "images"
  };

  state = {
    filters: null,
    view: "row",
    activeImage: null,
    redirect: null
  };

  // -- PAGE DATA MANAGEMENT -- //

  static getDerivedStateFromProps(props, state) {
    const { location } = props;

    const redirect = state.redirect === location.search ? null : state.redirect;

    return {
      ...state,
      filters: getURLFilterParams(location),
      redirect
    };
  }

  componentDidMount() {
    const { location, getImages, listImagesAvailability } = this.props;

    if (listImagesAvailability.available) {
      const searchParams = location.search
        ? `${location.search}&@type=golden`
        : "?@type=golden";
      getImages(searchParams);
    }
  }

  componentDidUpdate(prevProps) {
    const { location, getImages, listImagesAvailability } = this.props;

    if (
      location.search !== prevProps.location.search &&
      listImagesAvailability.available
    ) {
      const searchParams = location.search
        ? `${location.search}&@type=golden`
        : "?@type=golden";

      getImages(searchParams);
    }
  }

  // -- HANDLERS & ACTIONS -- //

  onPageChange = (evt, { activePage }) => {
    const redirect = updatePageSearchParams(
      this.props.location.search,
      activePage
    ).toString();

    this.setState({
      redirect: `?${redirect}`
    });
  };

  onFilterChange = data => {
    const redirect = updateFilterSearchParams(
      this.props.location.search,
      data.filters
    ).toString();

    this.setState({
      view: data.view,
      filters: data.filters,
      redirect: `?${redirect}`
    });
  };

  getImageEntityById = id => {
    const { data } = this.props;
    return data.find(element => element["@id"] === id);
  };

  handleDelete = activeImage => {
    const { data, openModal, copyKey } = this.props;

    this.setState({ activeImage }, () => {
      openModal(
        this.doDelete,
        `${copyKey}.modals.delete_modal`,
        undefined,
        this.getImageEntityById(activeImage)
      );
    });
  };

  doDelete = () => {
    const { location, operationAction, data, getImages } = this.props;
    const { activeImage } = this.state;

    const entity = this.getImageEntityById(activeImage);

    operationAction(entity, OPERATIONS.DELETE, undefined, {
      success: getImages(`${location.search}&@type=golden`, true)
    });
  };

  // -- RENDER -- //

  renderHeaderSet() {
    const { view, filters } = this.state;
    const { copyKey, copy } = this.props;
    return (
      <Fragment>
        <ImageFilters
          onFilterChange={this.onFilterChange}
          initialValues={{ filters, view }}
        />
        <Header as="h3">{copy(`${copyKey}.title`)}</Header>
      </Fragment>
    );
  }

  renderRows() {
    const { data } = this.props;

    if (!data) return null;

    return data.map(entity => {
      return (
        <ImageRow
          key={entity.id}
          entity={entity}
          onDelete={this.handleDelete}
        />
      );
    });
  }

  render() {
    const { redirect } = this.state;
    const {
      page,
      totalPages,
      data,
      location,
      isFetching,
      listImagesAvailability: { permitted, enabled },
      copyKey,
      copy
    } = this.props;

    if (redirect) {
      return (
        <RedirectWithReset to={`${location.pathname}${redirect}`} push={true} />
      );
    }

    if (!permitted) {
      return <UnauthorisedFeature title={copy(`${copyKey}.title`)} />;
    }

    // early return if images isn't ready to render
    if (isFetching) {
      return (
        <Container>
          {this.renderHeaderSet()}
          <Loader active inline="centered" />
        </Container>
      );
    }

    // early return if no images are returned
    if (!enabled || !data || data.length < 1) {
      return (
        <Container>
          {this.renderHeaderSet()}
          <p>{copy(`${copyKey}.none_available`)}</p>
        </Container>
      );
    }

    return (
      <Container>
        {this.renderHeaderSet()}

        <Table celled basic striped selectable unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{copy(`${copyKey}.name`)}</Table.HeaderCell>
              <Table.HeaderCell />
              <MediaAs greaterThanOrEqual="tablet" as={Table.HeaderCell}>
                {copy(`${copyKey}.provider`)}
              </MediaAs>
              <Table.HeaderCell>{copy(`${copyKey}.regions`)}</Table.HeaderCell>
              <MediaAs greaterThanOrEqual="tablet" as={Table.HeaderCell}>
                {copy(`${copyKey}.created_at`)}
              </MediaAs>
              <Table.HeaderCell />
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>{this.renderRows()}</Table.Body>
        </Table>

        {totalPages && totalPages > 1 && (
          <Fragment>
            <Pagination
              defaultActivePage={page}
              totalPages={totalPages}
              firstItem={null}
              lastItem={null}
              onPageChange={this.onPageChange}
              className={"float-right"}
            />
            <Divider hidden fitted clearing />
          </Fragment>
        )}
      </Container>
    );
  }
}

const mapStateToProps = state => ({
  data: imageSelector.selectImages(state),
  page: state.ephemeralEntities.page,
  totalPages: state.ephemeralEntities.totalPages,
  isFetching: state.ephemeralEntities.isFetching,
  isErrored: state.api.isError,
  listImagesAvailability: selectOperationAvailability(state)(
    AT_TYPES.IMAGE, // needs updating once images are available
    OPERATIONS.LIST
  ),
  copy: selectCopy(state)
});

const mapDispatchToProps = dispatch => ({
  getImages: (...args) => dispatch(getImages(...args)),
  operationAction: (entity, operationName, data, options) =>
    dispatch(operationAction(ENTITIES, entity, operationName, data, options))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withConfirmationModal(Images));
